import React, { useEffect, useMemo } from 'react';
import get from 'lodash/get';
import * as PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Button from '../../Button';
import MetaHelmet from '../../AppHelmet/MetaHelmet';
import VisitOverview from '../Components/VisitOverview';
import AppointmentHeader from '../Components/AppointmentHeader';
import CompleteInlineVisitModal from '../CompleteInlineVisitModal';

import { useOverviewRows } from './useOverviewRows';
import { usePageView } from '../../../hocs/usePageView';
import useNavigateToPreviousPage from '../hooks/useNavigateToPreviousPage';
import {
  selectPreferredLanguage,
  selectUserPrimaryPatientProfileGuarantorId,
} from '../../../redux/appointmentForm/selectors';

import { INLINE_TYPES } from '../../../constants';

import {
  redirectToAdditionalInformation,
  redirectToAvailableTimeSlots,
  redirectToProviders,
  redirectToRescheduleProviderDetails,
} from '../../../utils/appointments/redirect-utils';
import convertAdditionalInfoDataToRequestPayload from '../../../utils/appointments/convert-additional-info-data-to-request-payload';
import { enhance } from './enhance';

import styles from './styles.styl';
import { gerPCPData } from '../../../utils/appointments/gerPCPData';

function AppointmentOverview({
  careUser,
  clearProgressConsents,
  confirmInlineVisit,
  confirmVisit,
  confirmVisitGW,
  consentsData,
  createEirUrgentCareAppointment,
  createEirUrgentCareAppointmentPending,
  fetchCopay,
  fetchScheduleIdPending,
  formatMessage,
  getAvailableTimeSlots,
  insuranceEligibilityId,
  isConfirmedInlineVisit,
  isConsentDataFetching,
  isEirVisitType,
  isGWFlow,
  pharmacyId,
  isRescheduleFlow,
  loading,
  loadingGW,
  loadingTimeSlots,
  location,
  locationId,
  organization,
  pcpFormData,
  price,
  programBasedVisitId,
  provider,
  reason,
  schedule,
  scheduleId,
  selfScheduleConfirmPending,
  service,
  setScheduleData,
  subtitle,
  timeSlots,
  visitType,
  additionalInfoFormData = {},
  showPaperworkAppointmentQuestionStep,
}) {
  const language = useSelector(selectPreferredLanguage);
  const guarantorUserId = useSelector(
    selectUserPrimaryPatientProfileGuarantorId,
  );

  const isInlineVisit = INLINE_TYPES.includes(visitType.code);

  const submitBtnTitle = isInlineVisit
    ? formatMessage({ id: 'submit.getInLine' })
    : formatMessage({ id: 'submit.fillProfile' });

  const cancelBtnTitle = isInlineVisit
    ? formatMessage({ id: 'common.changeTime' })
    : formatMessage({ id: 'common.cancel' });

  usePageView('Get Care | Appointment Overview');

  const { navigateToPrevPage } = useNavigateToPreviousPage();

  useEffect(() => {
    if (isInlineVisit) {
      getAvailableTimeSlots({
        reasonTypeId: reason.id,
        serviceId: service.id,
        locationId: location.id,
      });
    }
  }, [isInlineVisit, reason, service, location]);

  useEffect(() => {
    if (timeSlots && timeSlots.length > 0 && !schedule.time) {
      setScheduleData({ time: timeSlots[0] });
    }
  }, [timeSlots, schedule]);

  useEffect(() => {
    if (isInlineVisit || isEirVisitType) return;

    const timeSlotsSource =
      get(schedule, 'provider.timeSlotsSource', null) ||
      get(schedule, 'timeSlotsSource', null);

    fetchCopay({
      locationId,
      date: schedule.time,
      userId: careUser.id,
      serviceId: service.id,
      visitTypeId: visitType.id,
      insuranceEligibilityResultId: insuranceEligibilityId,
      timeSlotsSource,
    });
  }, [
    isInlineVisit,
    isEirVisitType,
    locationId,
    service.id,
    careUser.id,
    visitType.id,
    schedule.time,
    schedule.provider,
    insuranceEligibilityId,
    schedule.timeSlotsSource,
  ]);

  const makeAppointmentEntity = useMemo(
    () => () => ({
      service,
      providerInfo: provider,
      dateTime: schedule.time,
      consentsAssignment: {
        ...(consentsData && { ...consentsData }),
      },
    }),
    [consentsData, service, provider, schedule],
  );

  const { rows } = useOverviewRows({
    price,
    reason,
    service,
    provider,
    location,
    schedule,
    visitType,
    formatMessage,
    appointment: makeAppointmentEntity(),
  });

  const visitTypeName = isInlineVisit
    ? `${visitType.name} ${formatMessage({
        id: 'common.visit',
      })}`
    : visitType.name;

  const allLoadings =
    loading ||
    loadingGW ||
    fetchScheduleIdPending ||
    selfScheduleConfirmPending ||
    loadingTimeSlots ||
    createEirUrgentCareAppointmentPending ||
    isConsentDataFetching;

  const handleRedirect = inlineRedirect => {
    if (!isInlineVisit) clearProgressConsents();

    if (isRescheduleFlow) {
      return redirectToRescheduleProviderDetails({
        locationId,
        providerId: provider.id,
      });
    }

    if (isInlineVisit) {
      if (showPaperworkAppointmentQuestionStep) {
        redirectToAdditionalInformation();
        return;
      }

      inlineRedirect();
      return;
    }

    return redirectToProviders();
  };

  const handleCancel = () => handleRedirect(redirectToAvailableTimeSlots);

  const handleConfirm = () => {
    const additionalParams = reason.isHomeLocationDifferent
      ? {
          country: reason.country,
          state: reason.state,
        }
      : {};

    const convertedAdditionalInfoData = convertAdditionalInfoDataToRequestPayload(
      additionalInfoFormData,
    );

    if (isInlineVisit) {
      confirmInlineVisit({
        note: reason.note,
        serviceId: service.id,
        reasonTypeId: reason.id,
        organizationId: organization.id,
        locationId: location.id,
        dateTime: schedule.time,
        userId: careUser.id,
        ...additionalParams,
        ...(insuranceEligibilityId && {
          insuranceEligibilityResultId: insuranceEligibilityId,
        }),
        ...convertedAdditionalInfoData,
      });
    } else {
      const payload = {
        providerId: provider.id,
        visitTypeId: visitType.id,
        dateTime: schedule.time,
        id: careUser.id,
        ...additionalParams,
        ...(insuranceEligibilityId && {
          insuranceEligibilityResultId: insuranceEligibilityId,
        }),
        ...(isGWFlow && { schedulingId: scheduleId || schedule.id }),
        ...(!isGWFlow && { serviceId: service.id }),
        ...(!isGWFlow && { reasonTypeId: reason.id }),
        ...(!isGWFlow && { reasonNote: reason.note || null }),
        ...(!isGWFlow && { locationId }),
        ...(!isGWFlow && { organizationId: organization.id }),
        ...(!isGWFlow &&
          programBasedVisitId && { pbvConfigId: programBasedVisitId }),
        ...convertedAdditionalInfoData,
      };

      if (isEirVisitType) {
        createEirUrgentCareAppointment({
          ...payload,
          ...(pcpFormData && {
            pharmacy_id: pharmacyId,
            ...gerPCPData(pcpFormData),
          }),
          guarantor_user_id: guarantorUserId,
          language,
        });
      } else if (isGWFlow) {
        confirmVisitGW(payload);
      } else {
        confirmVisit(payload);
      }
    }
  };

  return (
    <div className={styles.appointment}>
      <MetaHelmet title="pages.title.getCare.overview" />

      <AppointmentHeader
        title={{
          text: isInlineVisit ? visitTypeName : visitType.name,
        }}
        subtitle={subtitle}
        handleBack={navigateToPrevPage}
      />
      <VisitOverview
        rows={rows}
        provider={provider}
        loading={allLoadings}
        withTopPositionDefault
        visitName={visitTypeName}
        organization={organization}
        isInlineVisit={isInlineVisit}
        isEirVisitType={isEirVisitType}
        renderBtn={() => (
          <div className={isInlineVisit ? styles.inlineVisitBtns : 'mt3 tr'}>
            <Button
              onClick={handleCancel}
              className="mr3"
              color="accent"
              raised
            >
              {cancelBtnTitle}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={allLoadings}
              color="primary"
              raised
            >
              {submitBtnTitle}
            </Button>
          </div>
        )}
      />
      <CompleteInlineVisitModal
        formatMessage={formatMessage}
        userId={careUser.id}
        isOpened={isConfirmedInlineVisit}
      />
    </div>
  );
}

AppointmentOverview.propTypes = {
  showPaperworkAppointmentQuestionStep: PropTypes.bool,
  careUser: PropTypes.object,
  clearProgressConsents: PropTypes.func,
  confirmInlineVisit: PropTypes.func,
  confirmVisit: PropTypes.func,
  confirmVisitGW: PropTypes.func,
  consentsData: PropTypes.object,
  createEirUrgentCareAppointment: PropTypes.func,
  createEirUrgentCareAppointmentPending: PropTypes.bool,
  fetchCopay: PropTypes.func,
  fetchScheduleIdPending: PropTypes.bool,
  formatMessage: PropTypes.func,
  getAvailableTimeSlots: PropTypes.func,
  insuranceEligibilityId: PropTypes.number,
  isConfirmedInlineVisit: PropTypes.bool,
  isConsentDataFetching: PropTypes.bool,
  isEirVisitType: PropTypes.bool,
  isGWFlow: PropTypes.bool,
  isRescheduleFlow: PropTypes.bool,
  loading: PropTypes.bool,
  loadingGW: PropTypes.bool,
  loadingTimeSlots: PropTypes.bool,
  location: PropTypes.object,
  pharmacyId: PropTypes.number,
  locationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  organization: PropTypes.object,
  pcpFormData: PropTypes.object,
  additionalInfoFormData: PropTypes.object,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  programBasedVisitId: PropTypes.number,
  provider: PropTypes.object,
  reason: PropTypes.object,
  schedule: PropTypes.object,
  scheduleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selfScheduleConfirmPending: PropTypes.bool,
  service: PropTypes.object,
  setScheduleData: PropTypes.func,
  subtitle: PropTypes.object,
  timeSlots: PropTypes.array,
  visitType: PropTypes.object,
};

export default enhance(AppointmentOverview);



// WEBPACK FOOTER //
// ./src/components/AppointmentForm/AppointmentOverview/index.jsx